/**
 * useKeyboardAwareInputs — global mobile-keyboard rescue.
 *
 * On mobile browsers the OS keyboard covers roughly half the screen when an
 * input gains focus. Some layouts (long lists, sticky headers, inputs at the
 * top of the viewport) leave the focused field partially or fully hidden,
 * forcing users to scroll manually before they can see what they're typing.
 *
 * This hook installs a lightweight document-level `focusin` listener that:
 *   1. Waits for the keyboard to finish animating (visualViewport `resize`).
 *   2. Checks whether the focused element is still visible above the visual
 *      viewport bottom (accounting for the keyboard).
 *   3. If not, scrolls the element into view with a small safety gap.
 *
 * A CSS custom property `--kb-inset` is also updated so any layout can pad
 * its bottom with `padding-bottom: calc(env(safe-area-inset-bottom) + var(--kb-inset, 0px))`.
 */
import { useEffect } from 'react';

const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]),' +
  'textarea, [contenteditable="true"], [contenteditable=""]';

export default function useKeyboardAwareInputs() {
  useEffect(() => {
    const vv = window.visualViewport;

    const updateKbInset = () => {
      if (!vv) return;
      // Difference between layout viewport and visual viewport = keyboard height.
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--kb-inset', `${Math.round(inset)}px`);
    };

    const scrollFocusedIntoView = (el) => {
      if (!el || !el.getBoundingClientRect) return;
      const rect = el.getBoundingClientRect();
      const vvHeight = vv ? vv.height : window.innerHeight;
      const vvTop = vv ? vv.offsetTop : 0;
      const safeGap = 24;
      const bottomOfVisible = vvTop + vvHeight - safeGap;
      const topOfVisible = vvTop + safeGap;
      const outOfView =
        rect.bottom > bottomOfVisible || rect.top < topOfVisible;
      if (!outOfView) return;
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch {
        el.scrollIntoView(false);
      }
    };

    const onFocusIn = (e) => {
      const target = e.target;
      if (!target || !target.matches || !target.matches(FOCUSABLE_SELECTOR)) return;
      // Wait for the OS keyboard animation. Two rAFs + a small timeout is a
      // pragmatic compromise across iOS Safari, Chrome Android and desktop.
      setTimeout(() => {
        requestAnimationFrame(() => {
          updateKbInset();
          scrollFocusedIntoView(target);
        });
      }, 260);
    };

    const onFocusOut = () => {
      // Clear the inset once no field is focused, so bottom paddings collapse.
      setTimeout(() => {
        if (!document.activeElement ||
            !document.activeElement.matches ||
            !document.activeElement.matches(FOCUSABLE_SELECTOR)) {
          document.documentElement.style.setProperty('--kb-inset', '0px');
        }
      }, 120);
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    if (vv) {
      vv.addEventListener('resize', updateKbInset);
      vv.addEventListener('scroll', updateKbInset);
    }
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (vv) {
        vv.removeEventListener('resize', updateKbInset);
        vv.removeEventListener('scroll', updateKbInset);
      }
    };
  }, []);
}
